import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, NgForm, Validators } from '@angular/forms';

import { get } from 'lodash';

import { CommonRegEx, UrlValidationService, IApplication } from '@nrwl/common-lib';

import {
  IComponentStateHistory,
  IValueChangedEvent,
  IWizardContentComponent
} from '../../../wizard.content';
import { REMOVED } from '../../../services/base.wizard.step';
import { ApplicationWizardService } from '../../../services/application/application.wizard.service';
import { APP_TYPE_KEY, ApplicationType } from './../app.review.page/review.component';

@Component({
  selector: 'app-basic-html',
  templateUrl: './application.basic.html',
  styleUrls: ['../new.application.style.scss', './application.basic.style.scss']
})
export class ApplicationBasicHtmlComponent implements IWizardContentComponent, OnInit {
  @Input() step: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() subStep: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() previousOutput: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;

  apps: IApplication[];
  names: string[];
  urls: string[];
  name: string;
  url: string;
  identity = [];
  appType: ApplicationType;
  isChecked = false;
  curlUrl: string;
  isValidUrl = false;
  urlValidationWarningMessage = '';
  constructor(
    private wizardService: ApplicationWizardService,
    private urlValidationService: UrlValidationService
  ) {}

  ngOnInit() {
    // this is important, since WizardStep logic relies on validity of attached form
    this.step.form = this.form;
    this.apps = this.wizardService.getApps();
    this.names = this.apps.map(a => a.name).filter(name => !!name);
    this.urls = this.apps.map(a => a.url).filter(url => !!url);

    this.name = undefined;
    this.url = undefined;
    this.curlUrl = undefined;
    this.identity = [];

    // determine type of application
    // and attempt populate inputs, in case user came here by pressing 'back'
    this.appType = get(this.previousOutput, APP_TYPE_KEY, ApplicationType.CLOUD_BASED);
    if (!this.wizardService.editMode) {
      if (this.appType === ApplicationType.CLOUD_BASED) {
        this.restoreNameValue();
        this.restoreUrlValue();
        this.restoreCurlWithUrlValue();
      } else {
        this.restoreNameValue();
        this.restoreIdentityValue();
      }
    } else {
      this.fillFormData();
    }
  }

  fillFormData(): void {
    this.name = get(this.previousOutput, 'name');
    this.url = get(this.previousOutput, 'url');
    this.identity = get(this.previousOutput, 'applicationServers');
    this.names = this.names.filter(name => name !== this.name);
    this.urls = this.urls.filter(url => url !== this.url);
    if (get(this.previousOutput, 'curlUrl')) {
      this.curlUrl = get(this.previousOutput, 'curlUrl');
      const availableUrls = this.curlUrl.match(CommonRegEx.URL_REGEX);
      if (availableUrls?.length > 0 && this.url === availableUrls[0].replace(/["']/g, '')) {
        this.isChecked = true;
      } else {
        this.curlUrl = '';
        this.isChecked = false;
      }
    }
  }

  restoreIdentityValue(): void {
    const identityValue = this.stateHistory(this.componentId, 'identityInput');
    if (identityValue && identityValue.value && identityValue.value !== REMOVED) {
      this.identity = identityValue.value;
    }
  }

  restoreNameValue(): void {
    const nameValue = this.stateHistory(this.componentId, 'nameInput');
    if (nameValue && nameValue.value && nameValue.value !== REMOVED) {
      this.name = nameValue.value;
    }
  }

  restoreUrlValue(): void {
    const urlValue = this.stateHistory(this.componentId, 'urlInput');
    if (urlValue && urlValue.value && urlValue.value !== REMOVED) {
      this.url = urlValue.value;
    }
  }
  restoreCurlWithUrlValue(): void {
    const curlValue = this.stateHistory(this.componentId, 'curlWithURLInput');
    if (curlValue && curlValue.value && curlValue.value !== REMOVED) {
      this.isChecked = true;
      this.curlUrl = curlValue.value;
    }
  }

  isCloudBased(): boolean {
    return this.appType === ApplicationType.CLOUD_BASED;
  }

  nameUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'nameInput',
      key: 'name',
      newValue: this.name,
      isValid: this.form.controls.name.valid
    });
  }

  urlUpdated(): void {
    const urlValidation = this.urlValidationService.isUrlValid(this.url);
    this.isValidUrl = !this.curlUrl ? urlValidation.isValidUrl : true;
    this.urlValidationWarningMessage = !this.curlUrl ? urlValidation.errorMessage : '';

    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'urlInput',
      key: 'url',
      newValue: this.url ? this.url.trim() : this.url,
      isValid: this.url ? this.form.controls.url.valid && this.isValidUrl : false
    });
    if (!this.curlUrl) {
      this.curlwithURLUpdated();
    }
  }

  curlwithURLUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'curlWithURLInput',
      key: 'curlUrl',
      newValue: this.curlUrl,
      isValid: this.curlUrl ? this.form.controls.curlUrl.valid : this.url ? true : false
    });
    if (this.curlUrl && this.form.controls['curlUrl'].valid) {
      const availableUrls = this.curlUrl.match(CommonRegEx.URL_REGEX);
      this.url = availableUrls?.length > 0 ? availableUrls[0].replace(/["']/g, '') : this.curlUrl;
      this.form.control.addControl('url', new UntypedFormControl(this.url, Validators.required));
      this.urlUpdated();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  identityUpdated(event: any): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'identityInput',
      key: event.key,
      newValue: event.newValue,
      isValid: event.isValid
    });
  }

  /**
   * Method to reset the form value for toggle button
   */
  public resetURLvalues(): void {
    const urlValue = this.stateHistory(this.componentId, 'urlInput');
    if (
      (urlValue && urlValue.value && urlValue.value !== REMOVED) ||
      (this.wizardService.editMode && !urlValue)
    ) {
      this.url = undefined;
      this.urlUpdated();
    }
    const curlWithURLValue = this.stateHistory(this.componentId, 'curlWithURLInput');
    if (
      (curlWithURLValue && curlWithURLValue.value && curlWithURLValue.value !== REMOVED) ||
      (this.wizardService.editMode && !curlWithURLValue)
    ) {
      this.curlUrl = undefined;
      this.curlwithURLUpdated();
    }
  }
}
